/* eslint-disable complexity */
import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CircleX } from 'lucide-react';
import React from 'react';

import { PageResponse } from '@/slw/shared/types/api.type';
import { AllModel, AllState, ListingsFilter } from '@/slw/entities/search-results/search-results.type';
import { HomeFilterType } from '@/slw/entities/home/home.model';

interface FilteredProps {
  filter: ListingsFilter;
  homeFilter: HomeFilterType;
  allState: UseQueryResult<PageResponse<AllState[]>, AxiosError<any, any>>;
  allModel: UseQueryResult<PageResponse<AllModel[]>, AxiosError<any, any>>;
  clearFiltered: (type: any, clearValue: any) => void;
}

const filteredClass = 'relative inline-block border-2 border-primary rounded-xs pl-1 pr-4 text-sm mb-2';
const btnClass = 'absolute -top-2 -right-2';

function FilteredComponent({ filter, homeFilter, allState, allModel, clearFiltered }: FilteredProps) {
  return (
    <div className='filtered space-x-3 space-y-2 border-b mb-4'>
      {!!filter.keyword && (
        <div className={filteredClass}>
          {filter.keyword}
          <button onClick={() => clearFiltered('keyword', filter.keyword)} type='button' className={btnClass}>
            <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
            <span className='hidden'>keyword</span>
          </button>
        </div>
      )}
      {!!filter.filterByTag &&
        !!filter.filterByTag.length &&
        !!(homeFilter.vehicleEra || []).length &&
        filter.filterByTag.map((item) => (
          <div className={filteredClass} key={item}>
            {(homeFilter.vehicleEra || []).find((i) => i.code === item)?.name}
            <button type='button' onClick={() => clearFiltered('filterByTag', item)} className={btnClass}>
              <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
              <span className='hidden'>filterByTag</span>
            </button>
          </div>
        ))}
      {!!filter.premiumSpotlight && (
        <div className={filteredClass}>
          {'Premium Spotlight'}
          <button type='button' onClick={() => clearFiltered('premiumSpotlight', true)} className={btnClass}>
            <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
            <span className='hidden'>premiumSpotlight</span>
          </button>
        </div>
      )}
      {!!filter.orBestOffer && (
        <div className={filteredClass}>
          {'Or best offer (OBO)'}
          <button type='button' onClick={() => clearFiltered('orBestOffer', true)} className={btnClass}>
            <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
            <span className='hidden'>orBestOffer</span>
          </button>
        </div>
      )}
      {!!filter.fromYear && (
        <div className={filteredClass}>
          {`From year: ${filter.fromYear}`}
          <button type='button' onClick={() => clearFiltered('fromYear', filter.fromYear)} className={btnClass}>
            <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
          </button>
        </div>
      )}
      {!!filter.toYear && (
        <div className={filteredClass}>
          {`To year: ${filter.toYear}`}
          <button type='button' onClick={() => clearFiltered('toYear', filter.toYear)} className={btnClass}>
            <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
          </button>
        </div>
      )}
      {!!filter.fromPrice && (
        <div className={filteredClass}>
          {`Lowest price: ${filter.fromPrice}`}
          <button type='button' onClick={() => clearFiltered('fromPrice', filter.fromPrice)} className={btnClass}>
            <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
          </button>
        </div>
      )}
      {!!filter.toPrice && (
        <div className={filteredClass}>
          {`Highest price: ${filter.toPrice}`}
          <button type='button' onClick={() => clearFiltered('toPrice', filter.toPrice)} className={btnClass}>
            <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
          </button>
        </div>
      )}
      {!!filter.fromOdometer && (
        <div className={filteredClass}>
          {`Lowest mileage: ${filter.fromOdometer}`}
          <button type='button' onClick={() => clearFiltered('fromOdometer', filter.fromOdometer)} className={btnClass}>
            <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
          </button>
        </div>
      )}
      {!!filter.toOdometer && (
        <div className={filteredClass}>
          {`Highest mileage: ${filter.toOdometer}`}
          <button type='button' onClick={() => clearFiltered('toOdometer', filter.toOdometer)} className={btnClass}>
            <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
          </button>
        </div>
      )}
      {!!filter.country &&
        !!filter.country.length &&
        !!(allState.data?.data || []).length &&
        filter.country.map((item) => (
          <div className={filteredClass} key={item}>
            {(allState.data?.data || []).find((i) => i.countrySlug === item)?.country}
            <button type='button' onClick={() => clearFiltered('country', item)} className={btnClass}>
              <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
              <span className='hidden'>country</span>
            </button>
          </div>
        ))}
      {!!filter.state &&
        !!filter.state.length &&
        !!(allState.data?.data || []).length &&
        filter.state.map((item) => (
          <div className={filteredClass} key={item}>
            {(allState.data?.data || []).find((i) => i.stateSlug === item)?.state}
            <button type='button' onClick={() => clearFiltered('state', item)} className={btnClass}>
              <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
              <span className='hidden'>state</span>
            </button>
          </div>
        ))}
      {!!filter.make &&
        !!filter.make.length &&
        !!(allModel.data?.data || []).length &&
        filter.make.map((item) => (
          <div className={filteredClass} key={item}>
            {(allModel.data?.data || []).find((i) => i.makeSlug === item)?.make}
            <button type='button' onClick={() => clearFiltered('make', item)} className={btnClass}>
              <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
              <span className='hidden'>make</span>
            </button>
          </div>
        ))}
      {!!filter.model &&
        !!filter.model.length &&
        !!(allModel.data?.data || []).length &&
        filter.model.map((item) => (
          <div className={filteredClass} key={item}>
            {(allModel.data?.data || []).find((i) => i.modelSlug === item)?.model}
            <button type='button' onClick={() => clearFiltered('model', item)} className={btnClass}>
              <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
              <span className='hidden'>model {item}</span>
            </button>
          </div>
        ))}
      {!!filter.transmission &&
        !!filter.transmission.length &&
        !!(homeFilter.transmission || []).length &&
        filter.transmission.map((item) => (
          <div className={filteredClass} key={item}>
            {(homeFilter.transmission || []).find((i) => i.code === item)?.name}
            <button type='button' onClick={() => clearFiltered('transmission', item)} className={btnClass}>
              <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
              <span className='hidden'>transmission</span>
            </button>
          </div>
        ))}
      {!!filter.engineHistory &&
        !!filter.engineHistory.length &&
        !!(homeFilter.engineHistory || []).length &&
        filter.engineHistory.map((item) => (
          <div className={filteredClass} key={item}>
            {(homeFilter.engineHistory || []).find((i) => i.code === item)?.name}
            <button type='button' onClick={() => clearFiltered('engineHistory', item)} className={btnClass}>
              <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
              <span className='hidden'>engineHistory</span>
            </button>
          </div>
        ))}
      {!!filter.engineCondition &&
        !!filter.engineCondition.length &&
        !!(homeFilter.engineCondition || []).length &&
        filter.engineCondition.map((item) => (
          <div className={filteredClass} key={item}>
            {(homeFilter.engineCondition || []).find((i) => i.code === item)?.name}
            <button type='button' onClick={() => clearFiltered('engineCondition', item)} className={btnClass}>
              <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
              <span className='hidden'>engineCondition</span>
            </button>
          </div>
        ))}
      {!!filter.seatMaterial &&
        !!filter.seatMaterial.length &&
        !!(homeFilter.seatMaterial || []).length &&
        filter.seatMaterial.map((item) => (
          <div className={filteredClass} key={item}>
            {(homeFilter.seatMaterial || []).find((i) => i.code === item)?.name}
            <button type='button' onClick={() => clearFiltered('seatMaterial', item)} className={btnClass}>
              <CircleX className='h-5 w-5' color='white' strokeWidth={1.5} fill='#bc9c67' />
              <span className='hidden'>seatMaterial</span>
            </button>
          </div>
        ))}
    </div>
  );
}

export const Filtered = React.memo(FilteredComponent);
